import { useMemo } from "react";

import { MeatballMenu } from "@covertree/ui";

import {
  RenewalBaseFragment,
  RenewalStatus,
  RenewalWithCheckReceivalFragment,
} from "@covertree/agent-portal-graphql";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { useRenewalActions } from "@providers/RenewalActionsProvider";
import { useCheckReceivalPermissions } from "@hooks/actions/useCheckReceivalPermissions";
import { useOpenPolicyDetails } from "@hooks/actions/useOpenPolicyDetails";
import { useRetryRenewalSendOffer } from "@hooks/actions/useRetryRenewalSendOffer";
import { useRetryRenewalIssuance } from "@hooks/actions/useRetryRenewalIssuance";
import { useRetryFailedRenewal } from "@hooks/actions/useRetryFailedRenewal";
import { useResendRenewalOffer } from "@hooks/actions/useResendRenewalOffer";

interface Props {
  renewal?: RenewalBaseFragment | RenewalWithCheckReceivalFragment;
  loading: boolean;
}

export function ActionMenu({ renewal, loading }: Props) {
  const { isInternalAgent, canSeeDiscardedRenewals, canRetryFailedRenewal } =
    useAccessPattern();

  const { canMarkRenewalAsPaid, canMarkRenewalAsUnpaid } =
    useCheckReceivalPermissions(renewal);
  const { retryRenewalSendOffer } = useRetryRenewalSendOffer();
  const { retryFailedRenewal } = useRetryFailedRenewal();
  const { resendRenewalOffer } = useResendRenewalOffer();
  const { retryIssuance } = useRetryRenewalIssuance();

  const {
    showRenewalDiscardModal,
    showMarkAsPaidModal,
    showMarkAsUnpaidModal,
  } = useRenewalActions();

  const openPolicyDetails = useOpenPolicyDetails();

  const actionMenuItems = useMemo(
    () => [
      {
        label: "View Policy Details",
        onClick: () => openPolicyDetails(renewal.policyLocator),
      },
      ...(canSeeDiscardedRenewals && renewal?.status === RenewalStatus.OfferSent
        ? [
            {
              label: "Discard Renewal",
              onClick: () => showRenewalDiscardModal(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      (renewal?.status === RenewalStatus.PricingFailed ||
        renewal?.status === RenewalStatus.UwFailed)
        ? [
            {
              label: "Retry",
              onClick: () => retryFailedRenewal(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canMarkRenewalAsPaid
        ? [
            {
              label: "Mark as Paid",
              onClick: () =>
                showMarkAsPaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
          ]
        : []),
      ...(canMarkRenewalAsUnpaid
        ? [
            {
              label: "Edit Invoice Settlement Description",
              onClick: () =>
                showMarkAsPaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
            {
              label: "Mark as Unpaid",
              onClick: () =>
                showMarkAsUnpaidModal(
                  renewal as RenewalWithCheckReceivalFragment
                ),
            },
          ]
        : []),
      ...(isInternalAgent && renewal?.status === RenewalStatus.OfferSent
        ? [
            {
              label: "Resend Offer",
              onClick: () => resendRenewalOffer(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      renewal?.status === RenewalStatus.OfferSentFailed
        ? [
            {
              label: "Retry Send Offer",
              onClick: () => retryRenewalSendOffer(renewal.renewalLocator),
            },
          ]
        : []),
      ...(canRetryFailedRenewal &&
      renewal?.status === RenewalStatus.PaymentFailed
        ? [
            {
              label: "Retry Issuance",
              onClick: () => retryIssuance(renewal.renewalLocator),
            },
          ]
        : []),
    ],
    [
      renewal,
      isInternalAgent,
      canMarkRenewalAsPaid,
      canMarkRenewalAsUnpaid,
      canRetryFailedRenewal,
    ]
  );

  if (!actionMenuItems.length) {
    return null;
  }

  return (
    <MeatballMenu
      disabled={!renewal && loading}
      actionMenuItems={actionMenuItems}
      wrapperProps={{
        sx: (theme) => ({
          position: "absolute",
          top: theme.spacing(2),
          right: theme.spacing(9),
          borderColor: theme.palette.grey[200],
          width: theme.spacing(5),
          height: theme.spacing(5),
          "& > button": {
            width: "100%",
            height: "100%",
          },
        }),
      }}
      border
      closeOnClick
    />
  );
}
